import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { PaginatorModule } from 'primeng/paginator';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { MessageService } from 'primeng/api';
import { UserService } from '../../layout/service/user.service';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Component({
    selector: 'app-show-video-material',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ToastModule,
        PaginatorModule,
        DialogModule,
        EditorModule
    ],
    providers: [MessageService],
    template: `
        <div class="px-6 py-8">
            <!-- Back Button -->
            <div class="mb-6">
                <p-button
                    (onClick)="goBack()"
                    severity="secondary"
                    icon="pi pi-arrow-left"
                    label="Kembali ke List Video"
                    size="small">
                </p-button>
            </div>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.3-4.3"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Cari video..."
                            [(ngModel)]="searchQuery"
                            (input)="filterVideos()"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    </div>
                    <div class="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-filter absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <select
                            [(ngModel)]="selectedCategory"
                            (change)="filterVideos()"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none">
                            <option value="">Semua Kategori</option>
                            <option value="Anamnesis">Anamnesis</option>
                            <option value="Pemeriksaan Fisik">Pemeriksaan Fisik</option>
                            <option value="Komunikasi">Komunikasi</option>
                            <option value="Keterampilan Prosedural">Keterampilan Prosedural</option>
                            <option value="Kegawatdaruratan">Kegawatdaruratan</option>
                        </select>
                    </div>
                    <div class="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                        </svg>
                        <select
                            [(ngModel)]="selectedLevel"
                            (change)="filterVideos()"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none">
                            <option value="">Semua Level</option>
                            <option value="UKNPDPD">UKNPDPD</option>
                            <option value="Preklinik">Preklinik</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- Main Video Player -->
            <div class="bg-white rounded-xl shadow-lg border border-gray-200 mb-8 overflow-hidden" *ngIf="currentVideo">
                <div class="relative" style="padding-bottom: 56.25%;">
                    <video
                        *ngIf="currentVideo && currentVideo.id && videoUrl"
                        controls
                        controlsList="nodownload"
                        disablePictureInPicture
                        class="absolute top-0 left-0 w-full h-full"
                        [src]="videoUrl"
                        preload="metadata"
                        [title]="currentVideo.name">
                    </video>
                    <div *ngIf="currentVideo && currentVideo.id && !videoUrl" class="absolute top-0 left-0 w-full h-full bg-gray-100 flex items-center justify-center">
                        <div class="text-center text-gray-500">
                            <i class="pi pi-spin pi-spinner text-4xl mb-2"></i>
                            <p>Memuat video...</p>
                        </div>
                    </div>
                    <div *ngIf="!currentVideo.id" class="absolute top-0 left-0 w-full h-full bg-gray-200 flex items-center justify-center">
                        <div class="text-center text-gray-500">
                            <i class="pi pi-video text-4xl mb-2"></i>
                            <p>Video tidak tersedia</p>
                        </div>
                    </div>
                </div>
                <div class="p-6">
                    <div class="flex items-start justify-between mb-4">
                        <div class="flex-1">
                            <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ currentVideo.name }}</h2>
                            <p class="text-gray-600 mb-4">{{ currentVideo.description || 'Deskripsi video belum tersedia' }}</p>
                        </div>
                        <p-button
                            *ngIf="isAuthorizedUser"
                            (onClick)="editVideo()"
                            severity="info"
                            icon="pi pi-pencil"
                            size="small"
                            pTooltip="Edit Video"
                            tooltipPosition="top">
                        </p-button>
                    </div>
                    <div class="flex items-center gap-4 flex-wrap">
                        <span class="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                            {{ currentVideo.kategori }}
                        </span>
                        <span
                            class="px-3 py-1 rounded-full text-sm font-semibold"
                            [ngClass]="getLevelClass(currentVideo.level)">
                            {{ currentVideo.level }}
                        </span>
                        <div class="flex items-center gap-2 text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock w-4 h-4">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            <span class="text-sm">{{ formatDuration(currentVideo.duration) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-800 mb-4">{{ filteredVideos.length }} Video Ditemukan</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                    *ngFor="let video of paginatedVideos"
                    class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition cursor-pointer group"
                    (click)="watchVideo(video)">
                    <div class="relative aspect-video">
                        <img *ngIf="video.thumbnail_url" [src]="video.thumbnail_url" [alt]="video.name" class="w-full h-full object-fill" loading="lazy">
                        <div *ngIf="!video.thumbnail_url" class="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                            <i class="pi pi-video" style="font-size: 4.5rem;color:white;"></i>
                        </div>
                        <!-- Play overlay for thumbnail -->
                        <div *ngIf="video.thumbnail_url" class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play w-16 h-16 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                        </div>
                        <div class="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs font-semibold">
                            {{ formatDuration(video.duration) }}
                        </div>
                    </div>
                    <div class="p-4">
                        <h3 class="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {{ video.name }}
                        </h3>
                        <p class="text-sm text-gray-600 mb-3 line-clamp-2">
                            {{ video.description || 'Deskripsi video belum tersedia' }}
                        </p>
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-semibold">
                                {{ video.kategori }}
                            </span>
                            <span
                                class="px-2 py-1 rounded text-xs font-semibold"
                                [ngClass]="getLevelClass(video.level)">
                                {{ video.level }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-8 flex justify-center" *ngIf="filteredVideos.length > itemsPerPage">
                <p-paginator 
                    [first]="first" 
                    [rows]="itemsPerPage" 
                    [totalRecords]="filteredVideos.length" 
                    [rowsPerPageOptions]="[9, 18, 27]" 
                    (onPageChange)="onPageChange($event)">
                </p-paginator>
            </div>

            <!-- Loading State -->
            <div *ngIf="loading" class="text-center py-12">
                <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
                <p class="mt-2 text-gray-600">Memuat video...</p>
            </div>
        </div>

        <!-- Floating Notes Button -->
        <button 
            class="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-50"
            (click)="showNotesDialog = true"
            pTooltip="Tambah Catatan Video"
            tooltipPosition="left">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" x2="8" y1="13" y2="13"></line>
                <line x1="16" x2="8" y1="17" y2="17"></line>
                <line x1="10" x2="8" y1="9" y2="9"></line>
            </svg>
        </button>

        <!-- Notes Dialog -->
        <p-dialog 
            [(visible)]="showNotesDialog" 
            [modal]="true" 
            [style]="{width: '50vw'}" 
            [draggable]="false" 
            [resizable]="false"
            header="Tambah Catatan Video">
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium mb-2 text-gray-700">Catatan Anda</label>
                    <p-editor [(ngModel)]="noteContent" [style]="{'height':'320px'}">
                    </p-editor>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <div class="flex gap-2 justify-end">
                    <button pButton label="Batal" class="p-button-text" (click)="showNotesDialog = false"></button>
                    <button pButton label="Simpan" (click)="saveVideoNote()" [disabled]="!noteContent || noteContent.trim() === ''"></button>
                </div>
            </ng-template>
        </p-dialog>

        <p-toast></p-toast>
    `
})
export class ShowVideoMaterial implements OnInit, OnDestroy {
    videoId: number = 0;
    currentVideo: any = null;
    allVideos: any[] = [];
    loading = false;
    searchQuery = '';
    selectedCategory = '';
    selectedLevel = '';
    isAuthorizedUser = false;
    currentPage: number = 1;
    itemsPerPage: number = 9;
    first: number = 0;
    showNotesDialog: boolean = false;
    noteContent: string = '';
    videoUrl: string = '';
    private routeSub: Subscription = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private messageService: MessageService
    ) {}

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadAllVideos();
        
        this.routeSub = this.route.paramMap.pipe(
            map(params => Number(params.get('id'))),
            distinctUntilChanged()
        ).subscribe(videoId => {
            this.videoId = videoId;
            this.loadVideoData();
            this.updateFilteredVideos();
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY' || user.ID_ACCESS_ROLE === 'HAGSJ76H98';
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.isAuthorizedUser = false;
        }
    }

    loadVideoData() {
        this.loading = true;
        this.userService.getVideoMaterial(this.videoId).then(
            (response) => {
                this.currentVideo = response.data || response;
                this.videoUrl = this.currentVideo.stream_url;
                this.loading = false;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat data video'
                });
                this.loading = false;
            }
        );
    }

    loadAllVideos() {
        this.userService.getVideoMaterials().then(
            (response) => {
                this.allVideos = (response.data || response || []);
                this.updateFilteredVideos();
            },
            (error) => {
                console.error('Error loading videos:', error);
            }
        );
    }

    updateFilteredVideos() {
        // Reset to first page when video changes
        this.currentPage = 1;
        this.first = 0;
    }

    get filteredVideos(): any[] {
        return this.allVideos.filter(video => {
            // Exclude current video
            if (video.id == this.videoId) return false;
            
            const matchesSearch = !this.searchQuery ||
                video.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                (video.description && video.description.toLowerCase().includes(this.searchQuery.toLowerCase()));

            const matchesCategory = !this.selectedCategory || video.kategori === this.selectedCategory;
            const matchesLevel = !this.selectedLevel || video.level === this.selectedLevel;

            return matchesSearch && matchesCategory && matchesLevel;
        });
    }

    get paginatedVideos(): any[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredVideos.slice(start, end);
    }

    filterVideos() {
        // Reset to first page when filtering
        this.currentPage = 1;
        this.first = 0;
    }

    onPageChange(event: any) {
        this.first = event.first;
        this.currentPage = event.page + 1;
        this.itemsPerPage = event.rows;
    }

    goBack() {
        this.router.navigate(['/app/video_material/list']);
    }

    watchVideo(video: any) {
        this.router.navigate(['/app/video_material/show', video.id]);
    }

    editVideo() {
        this.router.navigate(['/app/video_material/edit', this.videoId]);
    }

    getLevelClass(level: string): string {
        switch (level) {
            case 'UKNPDPD': return 'bg-blue-100 text-blue-700';
            case 'Preklinik': return 'bg-purple-100 text-purple-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    }

    formatDuration(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    saveVideoNote() {
        if (!this.noteContent || this.noteContent.trim() === '') {
            this.messageService.add({
                severity: 'warn',
                summary: 'Peringatan',
                detail: 'Catatan tidak boleh kosong'
            });
            return;
        }

        const userStr = localStorage.getItem('user');
        let userId = '1';
        if (userStr) {
            const user = JSON.parse(userStr);
            userId = user.ID_USER || '1';
        }

        const noteData = {
            USER_ID: userId,
            note: this.noteContent,
            video_material_id: this.videoId
        };

        this.userService.addNotes(noteData).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Berhasil',
                detail: 'Catatan video berhasil disimpan'
            });
            this.showNotesDialog = false;
            this.noteContent = '';
        }).catch(err => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Gagal menyimpan catatan'
            });
        });
    }
}
